// src/components/ProductLabel.js

import React from "react";
import {
  IoBookOutline,
  IoWarningOutline,
  IoTriangleOutline,
} from "react-icons/io5";
import { MdDoNotDisturb } from "react-icons/md";

const MEDIA_HOST = "https://super-backend.hira-tech.co.uk";

// GTIN barcode image: full URL (backend may send a path or a full URL)
const resolveImageUrl = (path) => {
  if (!path || typeof path !== "string") return null;
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  return `${MEDIA_HOST}${path.startsWith("/") ? "" : "/"}${path}`;
};

// ✅ Picker shown above the label for Ti-base / Scanbody products:
// shows the product category and lets the user pick which screw goes with it.
export const ScrewPicker = ({ product, needsScrew, screwOptions = [], value, onChange }) => {
  if (!needsScrew) return null;

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "360px",
        marginTop: "6px",
        fontSize: "13px",
        textAlign: "left",
      }}
    >
      <div style={{ marginBottom: "4px" }}>
        Category:{" "}
        <b>{product?.product_category?.category_Name || "N/A"}</b>
      </div>

      {screwOptions.length > 0 ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: "100%",
            padding: "6px 8px",
            fontSize: "13px",
            borderRadius: "5px",
            border: "1px solid #ccc",
          }}
        >
          <option value="">Select screw (SKU)</option>
          {screwOptions.map((screw) => (
            <option key={screw.product_Id} value={String(screw.product_Id)}>
              {screw.SKU} - {screw.product_Name}
            </option>
          ))}
        </select>
      ) : (
        <small style={{ color: "#94a3b8" }}>
          No screw found in this category
        </small>
      )}
    </div>
  );
};

export const ProductLabel = ({ product, type = "full", screwSku = "" }) => {
  const currentDate = new Date().toISOString().split("T")[0];

  // =====================================================
  // UDI (GTIN + barcode image) — top right, next to DATE / REF / LOT rows
  // Layout: [UDI tag] [barcode image] [text lines]
  // =====================================================
  const gtin = String(product?.gtin || "").trim();
  const lotValue = String(product?.lot_number || "").trim();
  const gtinImageUrl = resolveImageUrl(product?.gtin_image);
  const hasUdi = !!(gtin || gtinImageUrl);

  // (11) = date shown on the label, as YYMMDD
  const yymmdd = currentDate.slice(2).replace(/-/g, "");
  const udiLines = [];
  if (gtin) udiLines.push(`(01)${gtin}`);
  if (lotValue) udiLines.push(`(10)${lotValue}`);
  udiLines.push(`(11)${yymmdd}`);

  const UDI_TAG_PX = 11;
  // image height = date row + LOT row (+ small gap above the icons)
  const UDI_IMAGE_PX = screwSku ? 21 : 24;
  const UDI_TEXT_PX = 37; // text column width
  const longestLine = Math.max(...udiLines.map((l) => l.length));
  // biggest font (max 4.8px) that still fits the text column
  const udiFont = Math.min(4.8, UDI_TEXT_PX / (longestLine * 0.6));

  // =====================================================
  // FULL LABEL — 60mm × 40mm
  // =====================================================
  const fullLabel = (
    <div
      id="print-label"
      style={{
        width: "60mm",
        height: "40mm",
        backgroundColor: "white",
        padding: "1.5mm 2mm 1mm 2mm",
        boxSizing: "border-box",
        fontFamily: "Arial, sans-serif",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        overflow: "hidden",
        color: "black",
        WebkitTextStroke: "0.2px black",
      }}
    >
      {/* ================= TOP: NAME + SKU ================= */}
      <div
        style={{
          flexShrink: 0,
          margin: 0,
          padding: 0,
        }}
      >
        {/* PRODUCT NAME */}
        <div
          style={{
            width: "100%",
            textAlign: "left",
            fontWeight: "900",
            fontSize: "7.5px",
            fontFamily: "Arial Black, Arial, sans-serif",
            letterSpacing: "0.2px",
            lineHeight: "1.05",
            margin: "0",
            padding: "0",
            maxHeight: "24px",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            wordBreak: "break-word",
          }}
        >
          {product?.product_Name || "Product Name"}
        </div>

        {/* SKU — alone, right aligned */}
        <div
          style={{
            width: "100%",
            textAlign: "right",
            margin: "1px 0 0 0",
            padding: "0",
          }}
        >
          <div
            style={{
              fontWeight: "900",
              fontSize: "6px",
              fontFamily: "Arial Black, Arial, sans-serif",
              letterSpacing: "0.2px",
              whiteSpace: "nowrap",
              lineHeight: "8px",
              height: "8px",
            }}
          >
            SKU: {product?.SKU || "N/A"}
          </div>
        </div>

        {/* SCREW SKU — below product SKU, right aligned */}
        {screwSku && (
          <div
            style={{
              width: "100%",
              textAlign: "right",
              margin: "0",
              padding: "0",
            }}
          >
            <div
              style={{
                fontWeight: "900",
                fontSize: "6px",
                fontFamily: "Arial Black, Arial, sans-serif",
                letterSpacing: "0.2px",
                whiteSpace: "nowrap",
                lineHeight: "8px",
                height: "8px",
              }}
            >
              SCREW SKU: {screwSku}
            </div>
          </div>
        )}

        {/* DIVIDER */}
        <hr
          style={{
            border: "0",
            borderTop: "1.5px solid black",
            width: "100%",
            margin: "1px 0 2px 0",
            padding: "0",
          }}
        />
      </div>

      <div style={{ position: "relative", width: "100%", flexShrink: 0 }}>
      {/* ===== UDI (top right): [UDI tag] [barcode image] [(01) GTIN, (10) LOT, (11) DATE] ===== */}
      {hasUdi && (
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            height: `${UDI_IMAGE_PX}px`,
            display: "flex",
            alignItems: "center",
            gap: "1px",
            zIndex: 1,
          }}
        >
          <div
            style={{
              width: `${UDI_TAG_PX}px`,
              marginRight: "4px",
              flexShrink: 0,
              textAlign: "center",
              border: "1px solid black",
              boxSizing: "border-box",
              fontFamily: "Arial, sans-serif",
              fontWeight: "900",
              fontSize: "4px",
              lineHeight: "6px",
            }}
          >
            UDI
          </div>

          {gtinImageUrl && (
            <img
              src={gtinImageUrl}
              alt="UDI"
              style={{
                width: `${UDI_IMAGE_PX}px`,
                height: `${UDI_IMAGE_PX}px`,
                objectFit: "contain",
                display: "block",
                flexShrink: 0,
              }}
            />
          )}

          <div
            style={{
              width: `${UDI_TEXT_PX}px`,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {udiLines.map((line, i) => (
              <div
                key={i}
                style={{
                  fontFamily: "Arial, sans-serif",
                  fontWeight: "900",
                  fontSize: `${udiFont}px`,
                  lineHeight: "6px",
                  whiteSpace: "nowrap",
                }}
              >
                {line}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ================= DATE (left) + REF ================= */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          height: "11px",
          margin: "0",
          padding: "0",
          lineHeight: "1",
          flexShrink: 0,
          position: "relative",
        }}
      >
        {/* DATE — left */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "2px",
            lineHeight: "1",
          }}
        >
          {/* Factory-shaped date icon */}
          <img
            src="/date1.png"
            alt="Date"
            style={{
              width: "11px",
              height: "11px",
              objectFit: "contain",
              display: "block",
              flexShrink: 0,
            }}
          />

          <span
            style={{
              fontWeight: "900",
              fontSize: "6px",
              letterSpacing: "0.2px",
              lineHeight: "11px",
              whiteSpace: "nowrap",
            }}
          >
            {currentDate}
          </span>
        </div>

        {/* REF (thoda sa left) */}
        <span
          style={{
            position: "absolute",
            left: "21.5mm",
            fontWeight: "900",
            fontSize: "6px",
            fontFamily: "Arial Black, Arial, sans-serif",
            letterSpacing: "0.2px",
            lineHeight: "11px",
            whiteSpace: "nowrap",
          }}
        >
          REF: {product?.reference || "N/A"}
        </span>
      </div>

      {/* ================= LOT ================= */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          height: "10px",
          margin: "0",
          padding: "0",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1.5px solid black",
            borderRadius: "1px",
            height: "9px",
            padding: "0 3px",
            fontSize: "5.5px",
            fontWeight: "900",
            fontFamily: "Arial Black, Arial, sans-serif",
            letterSpacing: "0.2px",
            lineHeight: "1",
            boxSizing: "border-box",
          }}
        >
          LOT
        </div>

        <span
          style={{
            marginLeft: "2px",
            fontSize: "5.5px",
            fontWeight: "900",
            fontFamily: "Arial Black, Arial, sans-serif",
            letterSpacing: "0.2px",
            lineHeight: "1",
          }}
        >
          N/A
        </span>
      </div>

      </div>

      {/* ================= ICONS — edge to edge ================= */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          margin: screwSku ? "1px 0 0 0" : "4px 0 0 0",
          padding: "0",
          flexShrink: 0,
        }}
      >
        {/* WARNING */}
        <IoWarningOutline
          style={{
            width: "30px",
            height: "30px",
            flexShrink: 0,
            color: "black",
          }}
        />

        {/* BOOK + i */}
        <div
          style={{
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "30px",
            height: "30px",
            flexShrink: 0,
          }}
        >
          <IoBookOutline
            style={{
              width: "30px",
              height: "30px",
              color: "black",
            }}
          />

          <span
            style={{
              position: "absolute",
              top: "50%",
              left: "72%",
              transform: "translate(-50%, -60%)",
              fontSize: "8px",
              fontWeight: "900",
              lineHeight: "1",
              color: "black",
            }}
          >
            i
          </span>
        </div>

        {/* NON STERILE */}
        <div
          style={{
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "30px",
            height: "30px",
            flexShrink: 0,
          }}
        >
          <IoTriangleOutline
            style={{
              width: "30px",
              height: "30px",
              color: "black",
            }}
          />

          <span
            style={{
              position: "absolute",
              top: "58%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              fontSize: "3.5px",
              fontWeight: "900",
              lineHeight: "1",
              textAlign: "center",
              width: "80%",
              color: "black",
            }}
          >
            Non
            <br />
            Sterile
          </span>
        </div>

        {/* DO NOT REUSE — 2 */}
        <div
          style={{
            position: "relative",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "30px",
            height: "30px",
            flexShrink: 0,
          }}
        >
          <MdDoNotDisturb
            style={{
              width: "30px",
              height: "30px",
              color: "black",
            }}
          />

          <span
            style={{
              position: "absolute",
              top: "52%",
              left: "50%",
              transform: "translate(-50%, -60%)",
              fontSize: "22px",
              color: "black",
              fontWeight: "900",
              lineHeight: "1",
            }}
          >
            2
          </span>
        </div>
      </div>

      {/* ================= BOTTOM ================= */}
      <div
        style={{
          flexShrink: 0,
          margin: "auto 0 0 0",
          padding: 0,
        }}
      >
        {/* DIVIDER */}
        <hr
          style={{
            border: "0",
            borderTop: "1.5px solid black",
            width: "100%",
            margin: "0 0 2px 0",
            padding: "0",
          }}
        />

        {/* HIRA TECH */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            height: "11px",
            margin: "0",
            padding: "0",
            flexShrink: 0,
          }}
        >
          <img
            src="/crowbar.jpg"
            style={{
              width: "9px",
              height: "9px",
              objectFit: "contain",
              display: "inline-block",
              marginRight: "2px",
              flexShrink: 0,
            }}
            alt="HIRA-TECH"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />

          <span
            className="label-company"
            style={{
              fontFamily: "Arial Black, Arial, sans-serif",
              fontSize: "5.5px",
              fontWeight: "900",
              letterSpacing: "0.15px",
              lineHeight: "1",
              whiteSpace: "nowrap",
            }}
          >
            Hira-Tech Limited
          </span>
        </div>

        {/* ADDRESS */}
        <div
          className="label-address"
          style={{
            textAlign: "left",
            fontSize: "5px",
            fontWeight: "900",
            fontFamily: "Arial Black, Arial, sans-serif",
            lineHeight: "1",
            margin: "1px 0 0 0",
            padding: "0",
            letterSpacing: "0.1px",
            flexShrink: 0,
          }}
        >
          188–190 Lythalls Lane,
          <br />
          Coventry, CV6 6FT, United Kingdom
        </div>

        {/* MADE IN */}
        <div
          style={{
            textAlign: "right",
            margin: "1px 0 0 0",
            padding: "0",
            lineHeight: "1",
            flexShrink: 0,
          }}
        >
          <span
            className="made-in-text"
            style={{
              fontWeight: "900",
              fontSize: "5px",
              borderBottom: "1.5px solid black",
              display: "inline-block",
              fontFamily: "Arial Black, Arial, sans-serif",
              paddingBottom: "1px",
              letterSpacing: "0.3px",
              lineHeight: "1",
            }}
          >
            Made in UK
          </span>
        </div>
      </div>
    </div>
  );

  // =====================================================
  // OPEN LABEL — 40mm × 20mm
  // =====================================================
  const openLabel = (
    <div
      id="print-label"
      style={{
        width: "40mm",
        height: "20mm",
        backgroundColor: "white",
        padding: "5px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          textAlign: "left",
          fontWeight: "900",
          fontSize: "0.5rem",
          fontFamily: "Arial Black",
          marginTop: "3px",
          letterSpacing: "0.8px",
        }}
      >
        {product?.product_Name || ""}
      </div>

      <div
        style={{
          textAlign: "left",
          fontWeight: "900",
          fontSize: "0.45rem",
          fontFamily: "Arial Black",
          marginTop: "1px",
          letterSpacing: "0.5px",
        }}
      >
        {product?.SKU || ""}
      </div>

      {screwSku && (
        <div
          style={{
            textAlign: "left",
            fontWeight: "900",
            fontSize: "0.45rem",
            fontFamily: "Arial Black",
            marginTop: "1px",
            letterSpacing: "0.5px",
          }}
        >
          Screw: {screwSku}
        </div>
      )}
    </div>
  );

  // =====================================================
  // RETURN
  // =====================================================
  return type === "full" ? fullLabel : openLabel;
};